<template>
  <b-container class="mw-100 mt-3 mb-2">
    <b-card>
      <validation-observer ref="mainForm" v-slot="{ invalid }">
      <b-container class="m-0 p-0 mw-100">
        <b-row>
          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <b-form-group :label="`${$tc('message.vehicle', 1)} (*)`"
                          label-for="input-vehicle">
              <model-list-select id="input-vehicle"
                                 option-text="text"
                                 option-value="value"
                                 :customText="e => `${e.text} ${e.enabled ? '' : this.$tc('message.disabled', 1)}`"
                                 :isDisabled="!!vehicleId"
                                 :placeholder="vehiclesOption && vehiclesOption.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                 v-model="input.vehicleId"
                                 :list="vehiclesOption">
              </model-list-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <b-form-group :label="`${$tc('message.type', 1)} ${$tc('message.operation', 1)}`"
                          label-for="input-special">
              <model-list-select id="input-special"
                                 option-text="text"
                                 option-value="value"
                                 :isDisabled="alreadyExecuted"
                                 :placeholder="operationType && operationType.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                 v-model="input.special"
                                 :list="operationType">
              </model-list-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <b-form-group :label="`${$t('message.causal')} (*)`"
                          label-for="input-causal">
              <model-list-select id="input-causal"
                                 option-text="text"
                                 option-value="value"
                                 :isDisabled="alreadyExecuted"
                                 @input="showGroupsInfo(operationEvent.active, $event)"
                                 :placeholder="operationOptions && operationOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                 v-model="input.type"
                                 :list="operationOptions">
              </model-list-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <b-form-group>
              <label slot="label" class="m-0 p-0">
                {{$tc('message.date', 1)}} {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}}

                (<font-awesome-icon icon="info-circle"
                                    class="c-green hover-enabled"
                                    @click="showInfoModal(
                    `${$tc('message.date', 1)} ${$tc('message.operation', 1).toLowerCase()} ${$t('message.scheduled').toLowerCase()}`,
                    `km ${$tc('message.operation', 1).toLowerCase()} ${$t('message.scheduled').toLowerCase()}`
                    )"/>)
              </label>
              <b-input-group>
                <flat-pickr id="planned-date"
                            ref="plannedDate"
                            :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                            :class="changeFlatClass('plannedDate', checkPlannedData(input.plannedDate, input.plannedKm).class)"
                            v-model="input.plannedDate"
                            :config="plannedDateConfig"
                            :disabled="alreadyExecuted"
                            @on-change="plannedDateChanged">
                </flat-pickr>
                <div class="input-group-append">
                  <b-button @click="input.plannedDate = null"
                            :disabled="!input.plannedDate || alreadyExecuted">
                    <font-awesome-icon icon="trash"/>
                  </b-button>
                </div>
                <div v-if="checkPlannedData(input.plannedDate, input.plannedKm).state === false" class="my-invalid-feedback">
                  <h6>{{$tc('message.date', 1)}} {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                </div>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <validation-provider :name="$tc('message.km', 1).toLowerCase()"
                                 :rules="{ min_value: 0, max: 30}"
                                 v-slot="{ changed, valid, errors }">
            <b-form-group>
              <label slot="label" class="m-0 p-0">
                km {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}}

                (<font-awesome-icon icon="info-circle"
                                    class="c-green hover-enabled"
                                    @click="showInfoModal(
                    `${$tc('message.date', 1)} ${$tc('message.operation', 1).toLowerCase()} ${$t('message.scheduled').toLowerCase()}`,
                    `km ${$tc('message.operation', 1).toLowerCase()} ${$t('message.scheduled').toLowerCase()}`
                    )"/>)
              </label>
              <b-input-group append="km">
                <b-form-input id="input-plannedKm"
                              v-model="input.plannedKm"
                              autocomplete="off"
                              :placeholder="`${$tc('message.example', 1)}: 150000`"
                              type="number"
                              :disabled="alreadyExecuted"
                              @input="newPlannedKm = $event"
                              :state="checkPlannedData(input.plannedKm, input.plannedDate).state && (changed && valid || !changed)"
                              min="0"
                              @keypress="isNumber($event, input.plannedKm, true)">
                </b-form-input>
              </b-input-group>
              <div v-if="checkPlannedData(input.plannedKm, input.plannedDate).state === false" class="my-invalid-feedback">
                <h6>km {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}} {{$tc('message.required', 1).toLowerCase()}}</h6>
              </div>
              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
            </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="3" lg="3" xl="3" class="text-center">
            <label></label>
            <b-form-group class="mt-2">
              <switch-button v-model="operationEvent.active"
                             :color="{checked: '#249a80', unchecked: '#dc3545'}"
                             :sync="true"
                             :disabled="operationWasDone"
                             @change="showGroupsInfo($event.value, input.type)"
                             :width="200"
                             id="event-executed"
                             :height="32"
                             :font-size="15.5"
                             :speed="500"
                             :labels="true">
                <template slot="checked">
                  {{$tc('message.notification', 1)}} {{$tc('message.enabled', 3).toLowerCase()}}
                </template>

                <template slot="unchecked">
                  {{$tc('message.notification', 1)}} {{$tc('message.disabled', 3).toLowerCase()}}
                </template>
              </switch-button>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="3" lg="3" xl="3" class="text-center" v-if="!!vehicleId">
            <b-form-group :label="$tc('message.done', 1)"
                          label-for="input-executed" >
              <switch-button v-model="input.executed"
                             :color="{checked: '#249a80', unchecked: '#dc3545'}"
                             :sync="true"
                             id="input-executed"
                             :width="100"
                             :height="32"
                             :font-size="15.5"
                             :speed="500"
                             :labels="true">
                <template slot="checked">
                  {{$t('message.yes')}}
                </template>
                <template slot="unchecked">
                  {{$t('message.no')}}
                </template>
              </switch-button>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <transition name="scroll-fade">
              <b-form-group v-show="input.executed">
                <label slot="label" class="m-0 p-0">
                  {{$tc('message.date', 1)}} {{$tc('message.operation', 1).toLowerCase()}} {{$tc('message.done', 1).toLowerCase()}}
                  {{input.plannedDate ? '(*)' : ''}}
                </label>

                <b-input-group>
                  <flat-pickr id="input-execDate"
                              ref="execDate"
                              :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                              :class="changeFlatClass('execDate', checkExecData(input.plannedDate, input.execDate).class)"
                              v-model="input.execDate"
                              :config="execDateConfig"
                              :disabled="!input.executed"
                              @on-change="execDateConfigChanged">
                  </flat-pickr>
                  <div class="input-group-append">
                    <b-button @click="input.execDate = null"
                              :disabled="!input.execDate">
                      <font-awesome-icon icon="trash"/>
                    </b-button>
                  </div>
                  <div v-if="checkExecData(input.plannedDate, input.execDate).state === false" class="my-invalid-feedback">
                    <h6>{{$tc('message.date', 1)}} {{$tc('message.operation', 1).toLowerCase()}} {{$tc('message.done', 1).toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                  </div>
                </b-input-group>
              </b-form-group>
            </transition>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="3" xl="3">
            <transition name="scroll-fade">
              <b-form-group v-show="input.executed">
                <label slot="label" class="m-0 p-0">
                  km {{$tc('message.operation', 1).toLowerCase()}} {{$tc('message.done', 1).toLowerCase()}}
                  {{input.plannedKm ? '(*)' : ''}}
                </label>

                <validation-provider :name="$tc('message.km', 1).toLowerCase()"
                                     :rules="{ min_value: 0, max: 15}"
                                     v-slot="{ valid, errors }">
                <b-input-group>
                  <b-form-input id="input-execKm"
                                v-model="input.execKm"
                                :disabled="!input.executed"
                                autocomplete="off"
                                :placeholder="`${$tc('message.example', 1)}: 150000`"
                                :state="checkExecData(input.plannedKm, input.execKm).state && ((itemChanged && valid) || !itemChanged)"
                                min="0"
                                @keypress="isNumber($event, input.execKm, true)">
                  </b-form-input>
                  <div class="input-group-append">
                    <div class="input-group-text">km</div>
                  </div>
                  <div v-if="checkExecData(input.plannedKm, input.execKm).state === false" class="my-invalid-feedback">
                    <h6>{{$tc('message.km', 1).toLowerCase()}} {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}} {{$tc('message.required', 1).toLowerCase()}}</h6>
                  </div>
                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-input-group>
                </validation-provider>
              </b-form-group>
            </transition>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <validation-provider :name="$tc('message.note', 1).toLowerCase()"
                                 :rules="{ max: 250}"
                                 v-slot="{ changed, valid, errors }">
            <b-form-group :label="`${$tc('message.note', 2)}`"
                          label-for="service-notes">
              <b-form-textarea id="service-notes"
                               v-model="input.description"
                               :placeholder="`${$tc('message.example', 1)}: ABCD - 1234 - ABCD - 1234 - ABCD - 1234 - ABCD - 1234`"
                               :state="changed && !valid ? false : null"
                               rows="3"
                               type="text">
              </b-form-textarea>
              <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
            </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row class="mt-0">
          <b-col class="mt-3 justify-content-end d-flex">
            <!-- config[crud], create - read - update - delete -->
            <b-button class="b-c-green mr-2" v-on:click="saveOperation(false)"
                      id="save-button"
                      v-if="operationId ? config.u : config.c"
                      :disabled="checkSaveButton(true) || invalid">{{$t('message.save')}}
            </b-button>

            <!-- config[crud], create - read - update - delete -->
            <b-button class="b-c-green mr-2" v-on:click="saveOperation(true)"
                      id="save-close-button"
                      v-if="operationId ? config.u : config.c"
                      :disabled="checkSaveButton(true) || invalid">{{$t('message.saveClose')}}
            </b-button>

            <b-button class="b-c-green" v-on:click="pushBack()">{{$t('message.close')}}</b-button>
          </b-col>
        </b-row>
      </b-container>
      </validation-observer>
    </b-card>

    <b-container class="m-0 p-0 mw-100">
      <b-row>
        <b-col cols="12" sm="12" md="12" lg="6" xl="6">
          <!-- config[crud], create - read - update - delete -->
          <transition name="scroll-fade"
                      v-if="operationId ? config.u : config.c">
            <b-card class="mt-4" v-show="checkReschedule()">
              <b-container class="m-0 p-0 mw-100">
                <b-row class="border-bottom border-c-green">
                  <b-col>
                    <label class="c-green">
                      {{$t('message.addNewOperationByCause')}} '{{input.type ? find(operationOptions, obj => obj.value === input.type).text : ''}}' {{$t('message.forThisVehicle').toLowerCase()}}
                    </label>
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col>
                    <b-form-group>
                      <b-form-radio-group
                        id="radio-group-1"
                        v-model="rescheduleSelected"
                        :options="getRescheduleOption()"
                        name="radio-options"
                      ></b-form-radio-group>
                    </b-form-group>
                  </b-col>
                </b-row>

                <transition name="scroll-fade">
                  <b-row>
                    <b-col cols="12" sm="12" md="12" lg="6" xl="6">
                      <b-form-group v-show="rescheduleSelected === 'or'">
                        <label slot="label" class="m-0 p-0">
                          {{$tc('message.new', 1)}} {{$tc('message.date', 1).toLowerCase()}} {{$tc('message.operation', 1).toLowerCase()}}

                          (<font-awesome-icon icon="info-circle"
                                              class="c-green hover-enabled"
                                              @click="showInfoModal(
                              `${$tc('message.new', 1)} ${$tc('message.date', 1).toLowerCase()} ${$tc('message.operation', 1).toLowerCase()}`,
                              `${$tc('message.new', 1)} km ${$tc('message.operation', 1).toLowerCase()}`
                              )"/>)
                        </label>
                        <b-input-group>
                          <flat-pickr id="new-operation-date"
                                      :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                      v-model="newPlannedDate"
                                      :config="newPlannedDateConfig">
                          </flat-pickr>
                          <div class="input-group-append">
                            <b-button @click="newPlannedDate = null"
                                      :disabled="!newPlannedDate">
                              <font-awesome-icon icon="trash"/>
                            </b-button>
                          </div>
                          <div v-if="checkPlannedData(newPlannedDate, newPlannedKm).state === false" class="my-invalid-feedback">
                            <h6>{{$tc('message.new', 1)}} {{$tc('message.date', 1).toLowerCase()}} {{$tc('message.operation', 1).toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                          </div>
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" sm="12" md="12" lg="6" xl="6">
                      <b-form-group :label="`${$tc('message.new', 1)} km ${$tc('message.operation', 1).toLowerCase()} (*)`"
                                    label-for="input-new-plannedKm" v-show="rescheduleSelected === 'or'">
                        <label slot="label" class="m-0 p-0">
                          {{$tc('message.new', 1)}} km {{$tc('message.operation', 1).toLowerCase()}}

                          (<font-awesome-icon icon="info-circle"
                                              class="c-green hover-enabled"
                                              @click="showInfoModal(
                              `${$tc('message.new', 1)} ${$tc('message.date', 1).toLowerCase()} ${$tc('message.operation', 1).toLowerCase()}`,
                              `${$tc('message.new', 1)} km ${$tc('message.operation', 1).toLowerCase()}`
                              )"/>)
                        </label>
                        <b-input-group append="km">
                          <b-form-input id="input-new-plannedKm"
                                        v-model="newPlannedKm"
                                        autocomplete="off"
                                        :placeholder="`${$tc('message.example', 1)}: 150000`"
                                        type="number"
                                        :state="checkPlannedData(newPlannedKm, newPlannedDate).state"
                                        min="0">
                          </b-form-input>
                        </b-input-group>
                        <div v-if="checkPlannedData(newPlannedKm, newPlannedDate).state === false" class="my-invalid-feedback">
                          <h6>{{$tc('message.new', 1)}} {{$tc('message.date', 1).toLowerCase()}} {{$tc('message.operation', 1).toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                        </div>
                      </b-form-group>
                    </b-col>

                    <b-col class="mt-3">
                      <b-button class="b-c-green mr-2 float-right" id="reschedule-operation-button"
                                :disabled="checkSaveButton(false) || newRescheduleCheck()" @click="startReschedule()">
                        {{$t('message.reschedule')}}
                      </b-button>
                    </b-col>
                  </b-row>
                </transition>
              </b-container>
            </b-card>
          </transition>
        </b-col>

        <b-col cols="12" sm="12" md="12" :lg="checkReschedule() ? 6 : 12" :xl="checkReschedule() ? 6 : 12">
          <transition name="scroll-fade">
            <b-card class="mt-4" v-if="!!vehicleId && !checkSaveButton(false)">
              <b-container class="m-0 p-0 mw-100">
                <b-row class="border-bottom border-c-green mb-3">
                  <b-col>
                    <label class="c-green">
                      {{$tc('message.operation', 2)}} {{$t('message.scheduledButNoExecuted').toLowerCase()}}
                    </label>
                  </b-col>
                </b-row>

                <b-row v-for="operation in scheduledOperations" :key="operation.id">
                  <b-col v-if="scheduledOperations.length !== 0">
                    <label class="hover-enabled"
                           v-on:click="itemChanged
                     ? checkItemChangedModal(operation)
                     : (rescheduleSelected !== '' ? startReschedule({
                     name: 'RefreshPage',
                     params: {id: operation.id, name: 'EditOperation'},
                     query: {vehicle: vehicleId}
                     }) : $router.push({
                     name: 'RefreshPage',
                     params: {id: operation.id, name: 'EditOperation'},
                     query: {vehicle: vehicleId}
                     }))">
                      <font-awesome-icon icon="trash" size="lg" class="c-green mr-1"/>
                      <b> {{$tc('message.type', 1)}}: </b>{{operation.messageType.toLowerCase()}} -
                      <b> {{$t('message.causal')}}: </b>{{operation.messageCausal.toLowerCase()}} -
                      <b> {{$t('message.when')}}: </b>{{operation.messageWhen.toLowerCase()}}
                    </label>
                  </b-col>
                </b-row>

                <b-row v-if="scheduledOperations.length === 0">
                  <b-col cols="auto">
                    <label> {{$t('message.emptyText')}} </label>
                  </b-col>
                </b-row>
              </b-container>
            </b-card>
          </transition>
        </b-col>
      </b-row>
    </b-container>

    <b-modal ref="checkItemChanged" id="checkItemChanged">
      <h4>{{$t('message.unsavedChanges')}}</h4>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button class="mr-3 b-c-green"
                  v-if="operationId ? config.u : config.c"
                  v-on:click="saveOperation(false, false, {
                      name: 'RefreshPage',
                      params: {id: refreshingItem.id, name: 'EditOperation'},
                      query: {vehicle: vehicleId}
                      })">
          {{$t('message.save')}}
        </b-button>

        <b-button class="mr-3 b-c-green"
                  v-on:click="$router.push({
                      name: 'RefreshPage',
                      params: {id: refreshingItem.id, name: 'EditOperation'},
                      query: {vehicle: vehicleId}
                      })">
          {{$t('message.noSave')}}
        </b-button>

        <b-button class="b-c-green mr-3" v-on:click="closeModal()">{{$t('message.cancel')}}</b-button>
      </div>
    </b-modal>

    <b-modal ref="newOperationRedirectModal" id="new-operation-redirect-modal" size="lg"
             :title="`${$t('message.summary')} ${$tc('message.operation', 1).toLowerCase()}`">
      <b-container>
        <b-row v-if="rescheduledRedirect">
          <b-col class="mb-2">
            <h5 class="c-red">{{$t('message.unsavedChanges')}}</h5>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.numberPlate', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.plateNumber}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.brand', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.brand || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.model', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.model || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.society', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.userCompanyText || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.sector', 1)}} {{$tc('message.activity', 1).toLowerCase()}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.activitySectorText || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.property', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledVehicle.ownerText || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <div class="d-flex">
              <h6><b>{{$tc('message.type', 1)}}:</b></h6>
              <h6 class="ml-2">{{rescheduledObj.specialDesc || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <div class="d-flex">
              <h6><b>{{$t('message.causal')}}:</b></h6>
              <h6 class="ml-2 overflow-hidden">{{rescheduledObj.causalDesc || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <b-container class="m-0 p-0 mw-100 w-100">
              <b-row>
                <b-col cols="12" sm="8" md="8" lg="8" xl="8">
                  <h6><b>{{$tc('message.date', 1)}} {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}}:</b></h6>
                </b-col>

                <b-col>
                  <h6 v-if="rescheduledObj.parsedPlannedDate">{{rescheduledObj.parsedPlannedDate}}</h6>
                  <h6 v-else><b>- - - - - - - -</b></h6>
                </b-col>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <b-container class="m-0 p-0 mw-100 w-100">
              <b-row>
                <b-col cols="12" sm="8" md="8" lg="8" xl="8">
                  <h6><b>km {{$tc('message.operation', 1).toLowerCase()}} {{$t('message.scheduled').toLowerCase()}}:</b></h6>
                </b-col>

                <b-col>
                  <h6 v-if="rescheduledObj.plannedKm">{{rescheduledObj.plannedKm}}</h6>
                  <h6 v-else><b>- - - - - - - -</b></h6>
                </b-col>
              </b-row>
            </b-container>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="12" lg="3" xl="3" class="text-center">
            <b-form-group :label="$tc('message.notification', 1)" class="m-0"
                          label-for="event-executed">
              <switch-button v-model="rescheduleOperationEvent.active"
                             :color="{checked: '#249a80', unchecked: '#dc3545'}"
                             :sync="true"
                             :width="150"
                             id="reschedulr-event-executed"
                             :height="32"
                             :font-size="15.5"
                             :speed="500"
                             :labels="true">
                <template slot="checked">
                  {{$tc('message.enabled', 3)}}
                </template>

                <template slot="unchecked">
                  {{$tc('message.disabled', 3)}}
                </template>
              </switch-button>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="12" md="12" lg="6" xl="6">
            <b-form-group :label="`${$tc('message.note', 2)}`" class="m-0"
                          label-for="service-notes">
              <b-form-textarea id="service-notes"
                               v-model="rescheduledObj.description"
                               rows="2"
                               type="text">
              </b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex" v-if="rescheduledRedirect">
        <!-- config[crud], create - read - update - delete -->
        <b-button class="b-c-green mr-2"
                  v-if="operationId ? config.u : config.c"
                  v-on:click="saveOperation(false, true, rescheduledRedirect)">{{$t('message.saveAndContinue')}}</b-button>

        <b-button class="b-c-green mr-2" v-on:click="$router.push(rescheduledRedirect)">{{$t('message.continueNoSave')}}</b-button>

        <b-button class="b-c-green mr-3" v-on:click="closeModal()">{{$t('message.cancel')}}</b-button>
      </div>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex" v-if="!rescheduledRedirect">
        <!-- config[crud], create - read - update - delete -->
        <b-button class="b-c-green mr-2" id="new-operation-redirect-modal-save-close"
                  v-if="operationId ? config.u : config.c"
                  v-on:click="saveOperation(false, true, { name: 'Operation', query: {vehicle: input.vehicleId} })">
          {{$t('message.saveClose')}}
        </b-button>

        <b-button class="b-c-green mr-3" v-on:click="closeModal()" id="new-operation-redirect-modal-close">
          {{$t('message.cancel')}}
        </b-button>
      </div>
    </b-modal>

    <b-modal ref="infoModal" id="infoModal" :title="`${$t('message.info')}`"
             no-close-on-backdrop no-close-on-esc hide-header-close>
      <b-container>

        {{$t('info.operationMandatoryInfo1')}} <br>
        <b>{{dataInfo}}</b>
        {{$t('message.and').toLowerCase()}}
        <b>{{kmInfo}}</b>.<br>
        <br>
        {{$t('info.operationMandatoryInfo2')}}
        <b>{{$tc('message.notification', 2)}}</b>,
        {{$t('info.operationMandatoryInfo3')}}
        <b>{{$t('info.operationMandatoryInfo4')}}</b>,
        {{$t('info.operationMandatoryInfo5')}}
        <i>{{$t('message.reminder').toLowerCase()}}</i>
        {{$t('info.operationMandatoryInfo6')}}
        {{$t('info.operationMandatoryInfo7')}}.

      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button class="b-c-green" @click="$refs.infoModal.hide()"> Ok
        </b-button>
      </div>
    </b-modal>

    <b-modal ref="groupInfo" id="group-info" :title="`${$t('message.info')}`"
             no-close-on-backdrop no-close-on-esc hide-header-close>
      <b-container>
        <b-row>
          <b-col>
            <h6>{{$t('info.willBeEnabledNotification')}}.</h6>
          </b-col>
        </b-row>

        <b-row v-if="groupInfoItem && groupInfoItem.active">
          <b-col cols="12 mt-3" v-if="groupInfoItem.notificationGroup">
            <label class="c-green">
              {{$tc('message.notificationGroup', 1)}}:
              {{groupInfoItem.notificationGroup.descr}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"></b-row>

            <b-container class="max-height-200-px">
              <b-row v-for="item in groupInfoItem.notificationGroup.recipients" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12 mt-3" v-if="groupInfoItem.reminderGroup">
            <label class="c-green">
              {{$tc('message.reminderGroup', 1)}}: {{groupInfoItem.reminderGroup.descr}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"></b-row>

            <b-container class="max-height-200-px">
              <b-row v-for="item in groupInfoItem.reminderGroup.recipients" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12 mt-2 c-red" v-if="!groupInfoItem.reminderGroup && !groupInfoItem.notificationGroup">
            {{$t('message.noGroupConfigured')}}
          </b-col>
        </b-row>

        <b-row v-if="groupInfoItem.active" class="mt-2">
          <b-col>
            <h6>{{$t('info.toChangeConfig')}}:</h6>
            <h6 :class="`c-green hover-enabled h-c-blue mb-0 ${isMobile ? 'd-block' : 'd-inline-flex'}`"
                @click="$router.push(first(links))">{{$tc('message.causal', 1)}} {{$tc('message.operation', 2)}}</h6>
          </b-col>
        </b-row>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button class="b-c-green" id="group-info-ok-button" @click="$refs.groupInfo.hide()"> Ok
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { find, first, cloneDeep, orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { Italian } from 'flatpickr/dist/l10n/it.js'
import notificationType from '../../plugins/notification-field-name'

export default {
  name: 'EditOperation',
  created () {
    this.fetchData()
  },
  mounted () {
    this.$root.$on('resize', () => {
      this.checkWindowWidth()
    })
    this.checkWindowWidth()
  },
  updated () {
    this.$root.$on('change-lang', () => {
      if (this.$el.isConnected) {
        this.getOptions()
        this.updateLinks()
      }
    })
  },
  data () {
    const operationId = this.$route.params.id !== 'new' ? this.$route.params.id : null
    const vehicleId = this.$route.query.vehicle
    const genericDateConfig = {
      dateFormat: 'Y-m-d',
      mode: 'single',
      altFormat: 'D j M Y',
      altInput: true,
      locale: Italian,
      minDate: '',
      maxDate: '',
      disable: []
    }
    const config = this.$route.meta.config

    return {
      config,
      find,
      first,
      operationId,
      vehicleId,
      DateTime,
      alreadyExecuted: false,
      scheduledOperations: [],
      rescheduleSelected: '',
      operationWasDone: false,
      vehiclesOption: [],
      operationOptions: [],
      newMail: null,
      input: { special: false, plannedKm: null, plannedDate: null, execKm: null, execDate: null, type: '' },
      operationType: [],
      itemChanged: false,
      watcher: null,
      reschedulePushBack: false,
      newPlannedDate: null,
      newPlannedKm: null,
      plannedDateConfig: cloneDeep(genericDateConfig),
      execDateConfig: cloneDeep(genericDateConfig),
      newPlannedDateConfig: cloneDeep(genericDateConfig),
      refreshingItem: {},
      dataInfo: '',
      kmInfo: '',
      links: [
        '/configuration/operation',
        '/recipients'
      ],
      operationEvent: {},
      schedulingEvents: [],
      rescheduledObj: {},
      rescheduledVehicle: {},
      rescheduleEvent: {},
      companyOptions: [],
      activitySectorOptions: [],
      ownerOptions: [],
      rescheduleOperationEvent: {},
      rescheduledRedirect: false,
      recipientOptions: [],
      groupInfoItem: {},
      isMobile: false
    }
  },
  methods: {
    clickedLink (link) {
      const vehicleId = this.input.vehicleId
      if (link.href === '/organization/registry_vehicles' && vehicleId) {
        this.pushBack({ name: 'EditVehicle', params: { id: vehicleId } })
      } else if (link.href && link.href !== '/organization/registry_vehicles') {
        this.pushBack(link.href)
      }
    },
    checkWindowWidth () {
      const size = Vue.myGetWindowSize()
      this.isMobile = (size !== 'md' && size !== 'lg' && size !== 'xl')
    },
    checkWatcher () {
      this.$root.$emit('activeLoader', true)
      if (this.watcher) this.watcher()
      this.itemChanged = false
    },
    addWatcher () {
      this.$root.$emit('activeLoader', false)
      this.watcher = this.$watch('input', (val) => {
        this.plateNumberChanged(val.vehicleId)
        this.itemChanged = true
      }, { deep: true })
    },
    fetchData () {
      this.checkWatcher()
      this.getOptions().then(() => {
        this.getOperation()
      })
    },
    getOptions () {
      const axiosArray = []
      this.getOperationType()
      axiosArray.push(this.getEnum('operation', 'operationOptions', true, true))
      axiosArray.push(this.getVehicles())
      axiosArray.push(this.getRecipients())
      axiosArray.push(this.getSchedulingEvents())
      return Vue.myAll(axiosArray).then(r => r)
    },
    getOperationType () {
      this.operationType = [
        { value: false, text: `${this.$t('message.ordinary')}` },
        { value: true, text: `${this.$t('message.extraOrdinary')}` }
      ]
    },
    getEnum (path, obj, selectionMessage, hasRecipients) {
      const options = []
      return Vue.myGet(this, `/enums/${path}`, 'data').then((items) => {
        if (selectionMessage) {
          options.push({ value: '', text: items.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') })
        }
        items.forEach((item) => {
          if (hasRecipients) {
            options.push({
              value: item.id,
              text: item.descr,
              notificationRecipientId: item.notificationRecipientId,
              reminderRecipientId: item.reminderRecipientId
            })
          } else {
            options.push({ value: item.id, text: item.descr })
          }
        })
        if (obj) {
          this[obj] = options
        }
        return options
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getVehicles () {
      const editedVehicleOptions = []
      return Vue.myGet(this, '/vehicles?all=true', 'vehicles').then((vehicles) => {
        const operationId = this.operationId
        const vehicleId = this.vehicleId
        vehicles.forEach((vehicle) => {
          const brand = vehicle.type && vehicle.type.brand ? vehicle.type.brand : null
          const model = vehicle.type && vehicle.type.model ? vehicle.type.model : null
          const name = `${vehicle.plateNumber} ${brand ? '- ' + brand : ''} ${model ? '- ' + model : ''}`
          const editedVehicleOption = {
            value: vehicle.id,
            text: name,
            brand,
            model,
            userCompany: vehicle.userCompany,
            activitySector: vehicle.activitySector,
            plateNumber: vehicle.plateNumber,
            owner: vehicle.ownership && vehicle.ownership.owner,
            registrationDate: vehicle.registrationDate,
            enabled: !vehicle.disablingDate || false
          }
          if ((!operationId && editedVehicleOption.enabled) || (!!operationId && vehicle.id === vehicleId)) {
            editedVehicleOptions.push(editedVehicleOption)
          }
        })
        this.vehiclesOption = orderBy(editedVehicleOptions, 'text')
        return editedVehicleOptions
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getRecipients () {
      const editedRecipientOptions = [{ descr: this.$t('message.all'), value: '', recipients: [] }]
      return Vue.myGet(this, '/recipients_list', 'recipients').then((recipients) => {
        recipients.forEach(recipient => {
          editedRecipientOptions.push(recipient)
        })
        this.recipientOptions = editedRecipientOptions
        return editedRecipientOptions
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getSchedulingEvents (item) {
      this.schedulingEvents = []
      if (this.vehicleId === 'new') return true
      const srcEntity = 'srcEntity=operation'
      const serviceFilter = item && item.type ? `&${notificationType.OPERATION.TYPE}=${item.type}` : ''
      const vehicleSrc = `&srcObjectId=${this.operationId}`
      const dateFilter = item && item.expDate ? '&start_date=' + item.expDate : ''
      const url = `/scheduling?${srcEntity}${serviceFilter}${vehicleSrc}${dateFilter}`
      return Vue.myGet(this, url, 'events').then((events) => {
        this.schedulingEvents = events
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        this.addWatcher()
        return false
      })
    },
    getOperation () {
      if (this.operationId) {
        Vue.myGet(this, `/operations?vehicle=${this.vehicleId}`, 'operations').then((operations) => {
          const scheduledOperations = []
          operations.forEach((operation) => {
            const editedOperation = operation
            editedOperation.messageType = operation.special ? this.$t('message.extraOrdinary') : this.$t('message.ordinary')
            editedOperation.messageCausal = find(this.operationOptions, obj => obj.value === operation.type).text || null
            if (operation.plannedDate && operation.plannedKm) {
              editedOperation.messageWhen = `${this.$t('message.inDate').toLowerCase()} ${DateTime.fromSQL(operation.plannedDate).toFormat('dd/MM/yyyy')}
                  ${this.$t('message.or').toLowerCase()} ${this.$t('message.atKm').toLowerCase()} ${operation.plannedKm}`
            } else if (operation.plannedDate) {
              editedOperation.messageWhen = `${this.$t('message.inDate').toLowerCase()} ${DateTime.fromSQL(operation.plannedDate).toFormat('dd/MM/yyyy')}`
            } else if (operation.plannedKm) {
              editedOperation.messageWhen = `${this.$t('message.atKm').toLowerCase()} ${operation.plannedKm}`
            }
            if (!editedOperation.executed && editedOperation.id !== this.operationId) scheduledOperations.push(editedOperation)
          })
          this.scheduledOperations = orderBy(scheduledOperations, ['plannedDate', 'plannedKm'], ['asc', 'asc'])
          const foundedOperation = find(operations, obj => obj.id === this.operationId)
          this.input = foundedOperation
          this.alreadyExecuted = foundedOperation.executed
          this.operationWasDone = cloneDeep(this.input.executed)
          this.addWatcher()
          this.getCurrentEvent(foundedOperation, this.schedulingEvents)
        }).catch(error => {
          Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        })
      } else {
        this.scheduledOperations = []
        this.input = { special: false, plannedKm: null, plannedDate: null, execKm: null, execDate: null, type: '' }
        this.operationWasDone = false
        this.addWatcher()
      }
      if (this.vehicleId) {
        this.input.vehicleId = this.vehicleId
      }
      this.updateLinks()
    },
    getCurrentEvent (operation, events) {
      const foundedEvent = find(events, obj => obj.srcObjectId === operation.vehicleId &&
        obj.date === operation.plannedDate && obj.km === operation.plannedKm &&
        obj.srcFieldName === `${notificationType.OPERATION.TYPE}=${operation.type}`)
      this.operationEvent = foundedEvent || {}
    },
    getRescheduleOption () {
      return [
        { text: `${this.$t('message.in')} 3 ${this.$tc('message.month', 2).toLowerCase()}`, value: 3 },
        { text: `${this.$t('message.in')} 6 ${this.$tc('message.month', 2).toLowerCase()}`, value: 6 },
        { text: `${this.$t('message.in')} 12 ${this.$tc('message.month', 2).toLowerCase()}`, value: 12 },
        { text: `${this.$t('message.or')}`, value: 'or' }
      ]
    },
    plannedDateChanged (val) {
      this.updateDateConfig(val, this.input.execDate, this.input.vehicleId)
    },
    execDateConfigChanged (val) {
      this.updateDateConfig(this.input.plannedDate, val, this.input.vehicleId)
    },
    plateNumberChanged (val) {
      this.updateDateConfig(this.input.plannedDate, this.input.execDate, val)
    },
    updateDateConfig (plannedDateVal, execDateVal, vehicleId) {
      const plannedDate = plannedDateVal ? DateTime.fromSQL(plannedDateVal).toSQLDate() : null
      const execDate = execDateVal ? DateTime.fromSQL(plannedDateVal).toSQLDate() : null
      const today = DateTime.local().toISODate()
      const vehicleRegistrationDate = vehicleId && this.vehiclesOption && this.vehiclesOption.length !== 0
        ? find(this.vehiclesOption, obj => obj.value === vehicleId).registrationDate : null

      this.plannedDateConfig.minDate = vehicleRegistrationDate
      this.execDateConfig.minDate = plannedDate || vehicleRegistrationDate
      this.newPlannedDateConfig.minDate = vehicleRegistrationDate

      this.plannedDateConfig.maxDate = execDate
      this.execDateConfig.maxDate = today
    },
    checkSaveButton (checkItemChanged) {
      const changed = this.itemChanged
      const hasVehicleId = this.input.vehicleId
      const plannedDate = this.input.plannedDate
      const plannedKm = this.input.plannedKm
      const type = this.input.type
      const execKmError = this.checkExecData(this.input.plannedKm, this.input.execKm).state === false
      const execDateError = this.checkExecData(this.input.plannedDate, this.input.execDate).state === false
      const exec = this.input.executed

      if (checkItemChanged && !changed) return true
      if (!hasVehicleId) return true
      if (!type) return true
      if (!plannedDate && !plannedKm) return true
      if (exec) {
        if (execKmError || execDateError) return true
      }
      return false
    },
    updateScheduling (operation, isReschedule) {
      const event = this.operationEvent
      const foundedType = find(this.operationOptions, obj => obj.value === operation.type)

      let myMessage = ''
      const vehicle = find(this.vehiclesOption, obj => obj.value === operation.vehicleId)
      const vehicleName = vehicle && vehicle.text ? vehicle.text : this.$tc('message.emptyValue', 1)
      let expirationValues = ''
      expirationValues += ` ${operation.plannedDate
        ? this.$t('message.inDate').toLowerCase() + ' ' + DateTime.fromSQL(operation.plannedDate).toFormat('dd/MM/yyyy')
        : ''}`
      expirationValues += `${operation.plannedDate && operation.plannedKm
        ? ', ' + this.$t('message.or').toLowerCase() + ' ' : ''}${!operation.plannedKm ? '' : operation.plannedKm + ' km'}`
      const causal = (foundedType && foundedType.text) || this.$tc('message.emptyValue', 1)

      myMessage += `${this.$tc('message.operation', 1)} "${causal}"`
      myMessage += ` ${this.$tc('message.ofVehicle', 1).toLowerCase()} "${vehicleName}",`
      myMessage += `${expirationValues}.`

      const schedulingObj = {
        srcEntity: 'operation',
        srcFieldName: `${notificationType.OPERATION.TYPE}=${operation.type}`,
        srcObjectId: operation.vehicleId,
        reminderRecipientId: (foundedType && foundedType.reminderRecipientId) || null,
        notificationRecipientId: (foundedType && foundedType.notificationRecipientId) || null,
        date: operation.plannedDate,
        km: operation.plannedKm || null,
        message: myMessage,
        active: isReschedule ? true : (!operation.execKm && !operation.execDate ? event.active : false)
      }

      if (event && event.id) {
        return Vue.myDelete(this, `/scheduling/${event.id}`).then(() => {
          return this.updateSchedulingContinue(null, schedulingObj)
        }).catch(error => {
          Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
          return false
        })
      }
      return this.updateSchedulingContinue(event, schedulingObj)
    },
    updateSchedulingContinue (event, schedulingObj) {
      return Vue.putOrPost(this, event && !!event.id, `/scheduling${event && event.id ? '/' + event.id : ''}`, schedulingObj).then(() => {
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return true
      })
    },
    newRescheduleCheck () {
      return this.rescheduleSelected === 'or' ? !this.newPlannedDate && !this.newPlannedKm : !this.rescheduleSelected
    },
    startReschedule (redirect) {
      this.rescheduledRedirect = redirect || false
      this.rescheduledObj = cloneDeep(this.assembleOperation(true))
      this.rescheduleEvent = cloneDeep(this.operationEvent)
      const foundedType = find(this.operationOptions, obj => obj.value === this.rescheduledObj.type)
      const foundedVehicle = find(this.vehiclesOption, obj => obj.value === this.rescheduledObj.vehicleId)
      if (foundedVehicle) {
        const foundedCompany = foundedVehicle.userCompany ? find(this.companyOptions, obj => obj.value === foundedVehicle.userCompany) : null
        const foundedActivity = foundedVehicle.activitySector ? find(this.activitySectorOptions, obj => obj.value === foundedVehicle.activitySector) : null
        const foundedOwner = foundedVehicle.owner ? find(this.ownerOptions, obj => obj.value === foundedVehicle.owner) : null
        foundedVehicle.userCompanyText = foundedCompany ? foundedCompany.text : this.$tc('message.emptyValue', 1)
        foundedVehicle.activitySectorText = foundedActivity ? foundedActivity.text : this.$tc('message.emptyValue', 1)
        foundedVehicle.ownerText = foundedOwner ? foundedOwner.text : this.$tc('message.emptyValue', 1)
      }
      this.rescheduleOperationEvent = { active: true }
      this.rescheduledObj.causalDesc = foundedType ? foundedType.text : this.$tc('message.emptyValue', 1)
      this.rescheduledObj.specialDesc = this.rescheduledObj.special ? this.$t('message.extraOrdinary') : this.$t('message.ordinary')
      this.rescheduledObj.parsedPlannedDate = this.rescheduledObj.plannedDate ? DateTime.fromSQL(this.rescheduledObj.plannedDate).toFormat('dd/MM/yyyy') : this.$tc('message.emptyValue', 1)
      this.rescheduledVehicle = foundedVehicle
      this.$refs.newOperationRedirectModal.show()
    },
    closeModal () {
      this.$refs.checkItemChanged.hide()
      this.$refs.newOperationRedirectModal.hide()
      this.newMail = ''
      this.refreshingItem = {}
    },
    checkItemChangedModal (item) {
      this.$refs.checkItemChanged.show()
      this.refreshingItem = item
    },
    saveOperation (pushBack, isReschedule, pushTo) {
      const newOperation = this.assembleOperation(isReschedule || false)

      Vue.myGet(this, `/operations${newOperation.id && newOperation.id !== 'new' ? '?vehicle=' + newOperation.vehicleId : ''}`, 'operations').then((operations) => {
        let foundedOlderOperation
        if (operations && operations.length && operations.length !== 0) {
          foundedOlderOperation = find(operations, obj => parseInt(obj.plannedKm) === parseInt(newOperation.plannedKm) &&
            obj.plannedDate === newOperation.plannedDate && obj.type === newOperation.type &&
            obj.special === newOperation.special)
        }

        if (!foundedOlderOperation || (foundedOlderOperation.id === newOperation.id)) {
          const isPut = isReschedule ? false : !!newOperation.id
          Vue.putOrPost(this, isPut, `/operations${newOperation.id && !isReschedule ? '/' + newOperation.id : ''}`, newOperation).then((r) => {
            this.updateScheduling(r.data, isReschedule).then(() => {
              Vue.success(this, `${this.$tc('message.operation', 1)} ${this.$t(newOperation.id ? 'success.putSuccess' : 'success.postSuccess')}`)
              if (pushBack) {
                this.itemChanged = false
                this.pushBack()
              } else {
                this.$router.push(pushTo || { name: 'RefreshPage', params: { id: newOperation.id || r.data.id, name: 'EditOperation' }, query: { vehicle: this.input.vehicleId } })
              }
            })
          }).catch(error => {
            Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
          })
        } else {
          Vue.error(this, `${this.$tc('message.operation', 1)} ${this.$tc('message.alreadyExist', 1).toLowerCase()}`)
        }
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
      })
    },
    checkReschedule () {
      const operationId = this.operationId
      const input = this.input
      const alreadyExecuted = this.alreadyExecuted
      const disabledSaveButton = this.checkSaveButton(false)
      const foundedVehicle = input.vehicleId ? find(this.vehiclesOption, obj => obj.value === input.vehicleId) : null
      const vehicleActive = foundedVehicle ? foundedVehicle.enabled : true
      return operationId && input.type && input.executed && alreadyExecuted && input.special === false && !disabledSaveButton && vehicleActive && !this.itemChanged
    },
    assembleOperation (rescheduleObj) {
      const operation = {
        vehicleId: this.input.vehicleId,
        type: this.input.type || null,
        special: this.input.special || false,
        description: this.input.description || null,
        plannedKm: this.input.plannedKm ? parseInt(this.input.plannedKm) : null,
        plannedDate: this.input.plannedDate || null,
        execKm: this.input.execKm ? parseInt(this.input.execKm) : null,
        execDate: this.input.execDate || null,
        executed: this.input.executed || false
      }
      if (!operation.executed) {
        operation.execDate = null
        operation.execKm = null
      }
      if (this.operationId && !rescheduleObj) operation.id = this.operationId
      if (rescheduleObj) {
        operation.plannedKm = null
        switch (this.rescheduleSelected) {
          case 3:
            operation.plannedDate = DateTime.local().plus({ months: 3 }).toSQLDate()
            break

          case 6:
            operation.plannedDate = DateTime.local().plus({ months: 6 }).toSQLDate()
            break

          case 12:
            operation.plannedDate = DateTime.local().plus({ months: 12 }).toSQLDate()
            break

          case 'or':
            operation.plannedDate = this.newPlannedDate || null
            operation.plannedKm = this.newPlannedKm ? parseInt(this.newPlannedKm) : null
            break

          default:
            break
        }
        operation.executed = false
        operation.execDate = null
        operation.execKm = null
      }
      return operation
    },
    pushBack (obj) {
      const backObj = obj || { name: 'Operation' }
      if (this.itemChanged) this.$root.$emit('go-back-modal', backObj)
      else this.$router.push(backObj)
    },
    checkPlannedData (first, second) {
      const changed = this.itemChanged
      if (first) {
        return { class: 'form-control is-valid', state: true }
      } else if (!second && changed) {
        return { class: 'form-control is-invalid', state: false }
      }
      return { class: 'form-control', state: null }
    },
    checkExecData (first, second) {
      if (this.input.executed) {
        if (first && second) {
          return { class: 'form-control is-valid', state: true }
        } else if (first && !second) {
          return { class: 'form-control is-invalid', state: false }
        }
      }
      return { class: 'form-control', state: null }
    },
    changeFlatClass (ref, myClass) {
      if (this.$refs[ref] && this.$refs[ref].$el && this.$refs[ref].$el.parentElement &&
        this.$refs[ref].$el.parentElement.children && this.$refs[ref].$el.parentElement.children[1]) {
        this.$refs[ref].$el.parentElement.children[1].className = myClass
      }
      return myClass
    },
    showInfoModal (data, km) {
      this.dataInfo = data
      this.kmInfo = km
      this.$refs.infoModal.show()
    },
    showGroupsInfo (isActive, type) {
      this.itemChanged = true
      const foundedService = type && isActive ? find(this.operationOptions, obj => obj.value === type) : null
      const groupInfoItem = {
        active: isActive,
        notificationGroup: null,
        reminderGroup: null
      }
      if (foundedService && isActive) {
        if (foundedService.notificationRecipientId) {
          groupInfoItem.notificationGroup = find(this.recipientOptions, obj => obj.id === foundedService.notificationRecipientId)
        }
        if (foundedService.reminderRecipientId) {
          groupInfoItem.reminderGroup = find(this.recipientOptions, obj => obj.id === foundedService.reminderRecipientId)
        }
        this.groupInfoItem = groupInfoItem
        this.$refs.groupInfo.show()
      }
    },
    updateLinks () {
      const links = []
      this.links.forEach(link => {
        links.push({ href: link })
      })
      this.$root.$emit('change-links', links)
    },
    isNumber (evt, input, integerValue) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))){
        if((!integerValue && (charCode !== 46 || input.indexOf('.') !== -1)) || integerValue) {
          evt.preventDefault();
        }
      } else {
        return true;
      }
    }
  }
}
</script>

<style scoped>
  .scroll-fade-enter-active {
    transition: all 1s ease;
  }
  .scroll-fade-leave-active {
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .scroll-fade-enter, .scroll-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }

  .hover-enabled:hover {
    text-decoration: underline;
    color: #249a80;
  }

  .max-height-200-px {
    max-height: 200px;
    overflow-y: auto;
  }
</style>
